// Tableau des amortissements (/immobilisations/tableau) + exports PDF / XLS.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table2, Loader2, FileDown, FileSpreadsheet } from "lucide-react";

const fmt = (n) => (n != null ? Math.round(n).toLocaleString("fr-FR") : "0");
const API = process.env.REACT_APP_BACKEND_URL;

export default function ImmobilisationsTableau() {
  const [liste, setListe] = useState([]);
  const [selection, setSelection] = useState("toutes");
  const [chargement, setChargement] = useState(true);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const { data } = await api.get("/immobilisations");
      setListe(data.immobilisations || []);
    } catch { setListe([]); }
    finally { setChargement(false); }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const affichees = selection === "toutes" ? liste : liste.filter((i) => i.id === selection);

  const exporter = async (format) => {
    try {
      const params = selection === "toutes" ? "" : `?imm_id=${selection}`;
      const url = `${API}/api/immobilisations/tableau/export-${format}${params}`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `amortissements.${format === "xls" ? "xlsx" : "pdf"}`;
      a.click();
    } catch { toast.error("Export impossible."); }
  };

  return (
    <div className="fenua-fade-up" data-testid="page-immobilisations-tableau">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-lg bg-[#E8F4F4] flex items-center justify-center">
            <Table2 className="h-6 w-6 text-[#1D3D3D]" />
          </div>
          <h1 data-testid="titre-section" className="text-3xl font-black tracking-tight text-[#1D3D3D]">Tableau des amortissements</h1>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selection} onValueChange={setSelection}>
            <SelectTrigger data-testid="select-immo" className="w-[240px] h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="toutes">Toutes les immobilisations</SelectItem>
              {liste.map((i) => <SelectItem key={i.id} value={i.id}>{i.numero} — {i.libelle}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button data-testid="export-xls" variant="outline" onClick={() => exporter("xls")}><FileSpreadsheet className="h-4 w-4 mr-1" /> XLS</Button>
          <Button data-testid="export-pdf" variant="outline" onClick={() => exporter("pdf")}><FileDown className="h-4 w-4 mr-1" /> PDF</Button>
        </div>
      </div>

      {chargement ? (
        <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#4A8A8A]" /></div>
      ) : affichees.length === 0 ? (
        <div className="bg-white border border-[#4A8A8A]/20 rounded-lg p-10 text-center text-[#1D3D3D]/60">Aucune immobilisation.</div>
      ) : (
        <div className="space-y-6" data-testid="tableaux-amortissement">
          {affichees.map((imm) => (
            <div key={imm.id} className="bg-white border border-[#4A8A8A]/20 rounded-lg overflow-hidden">
              <div className="bg-[#2D5A5A] text-white px-4 py-2 font-semibold text-sm flex justify-between">
                <span>{imm.numero} — {imm.libelle}</span>
                <span className="font-normal opacity-90">{imm.methode === "lineaire" ? "Linéaire" : "Dégressif"} · {imm.duree_annees} ans</span>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#E8F4F4] text-[#1D3D3D] text-left">
                    <th className="px-3 py-2">Année</th><th className="px-3 py-2 text-right">Valeur début</th>
                    <th className="px-3 py-2 text-right">Dotation</th><th className="px-3 py-2 text-right">Cumul amort.</th>
                    <th className="px-3 py-2 text-right">VNC fin</th><th className="px-3 py-2 text-center">Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {imm.tableau.map((l) => (
                    <tr key={l.annee} className="border-b border-[#4A8A8A]/10">
                      <td className="px-3 py-2 font-medium">{l.annee}</td>
                      <td className="px-3 py-2 text-right">{fmt(l.valeur_debut)}</td>
                      <td className="px-3 py-2 text-right">{fmt(l.dotation)}</td>
                      <td className="px-3 py-2 text-right">{fmt(l.cumul_amortissements)}</td>
                      <td className="px-3 py-2 text-right">{fmt(l.valeur_nette_fin)}</td>
                      <td className="px-3 py-2 text-center"><span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Planifié</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
